import { useEffect, useRef, useState } from 'react';

interface FAQ { q: string; a: string; }

interface FAQSectionProps {
  faqs: FAQ[];
  title?: string;
  subtitle?: string;
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function FAQSection({
  faqs,
  title = 'Common Questions',
  subtitle = 'Everything you need to know before starting your program.',
}: FAQSectionProps) {
  const { ref, visible } = useReveal();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      ref={ref}
      className="py-28 px-6 lg:px-12 xl:px-16"
      style={{ background: '#0A0A0A' }}
    >
      <div className="max-w-5xl mx-auto">
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">FAQ</span>
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold mb-4 leading-tight">{title}</h2>
          <p className="text-white/40 text-base font-sans max-w-2xl">{subtitle}</p>
        </div>

        <div className="flex flex-col">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`border-b border-white/8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-start justify-between gap-6 py-7 text-left cursor-pointer group"
              >
                <h3 className={`font-serif text-lg font-semibold leading-snug transition-colors duration-200 ${open === i ? 'text-gold' : 'text-white/80 group-hover:text-white'}`}>
                  {faq.q}
                </h3>
                <div className={`w-8 h-8 flex items-center justify-center border shrink-0 mt-0.5 transition-all duration-300 ${open === i ? 'border-gold/50 bg-gold/10 rotate-45' : 'border-white/15'}`}>
                  <i className={`text-sm transition-colors duration-200 ${open === i ? 'ri-add-line text-gold' : 'ri-add-line text-white/40'}`} />
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${open === i ? 'max-h-48 pb-7' : 'max-h-0'}`}
              >
                <p className="text-white/45 text-sm font-sans leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
